/* ==========================================================================
   Voltage — theme UI script (no dependencies)
   Handles: drawers/overlay, navigation, mega menu, scroll reveal, quantity, facets, slideshow, gallery, share
   Cart state and mutations live in cart.js; the two communicate via custom events.
   ========================================================================== */
(function () {
  'use strict';

  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const all = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const one = (sel, root = document) => root.querySelector(sel);

  // Product titles can contain & " and other characters, so escape before interpolating into HTML
  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);

  /* ---------- Overlay ---------- */
  const overlay = {
    el: one('[data-overlay]'),
    show() {
      if (!this.el) return;
      this.el.hidden = false;
      requestAnimationFrame(() => this.el.setAttribute('data-visible', 'true'));
      document.body.style.overflow = 'hidden';
    },
    hide() {
      if (!this.el) return;
      this.el.setAttribute('data-visible', 'false');
      const done = () => {
        this.el.hidden = true;
        this.el.removeEventListener('transitionend', done);
      };
      this.el.addEventListener('transitionend', done);
      setTimeout(done, 500);
      if (!one('[data-drawer][data-open="true"]') && !one('.mobile-nav[data-open="true"]')) {
        document.body.style.overflow = '';
      }
    }
  };

  /* ---------- Generic drawer ---------- */
  function openDrawer(name) {
    const el = one(`[data-drawer="${name}"]`);
    if (!el) return;
    el.setAttribute('data-open', 'true');
    el.removeAttribute('aria-hidden');
    overlay.show();
    const focusable = one('button, a[href], input, [tabindex]:not([tabindex="-1"])', el);
    if (focusable) focusable.focus({ preventScroll: true });
  }

  function closeDrawer(name) {
    const el = name ? one(`[data-drawer="${name}"]`) : one('[data-drawer][data-open="true"]');
    if (!el) return;
    el.setAttribute('data-open', 'false');
    el.setAttribute('aria-hidden', 'true');
    if (!one('[data-drawer][data-open="true"]') && !one('.mobile-nav[data-open="true"]')) overlay.hide();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.getAttribute('data-drawer-close') || undefined);
    }
  });

  if (overlay.el) on(overlay.el, 'click', () => {
    closeDrawer();
    closeMobileNav();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      closeMobileNav();
      closeMegaMenus();
    }
  });

  /* ---------- Mobile nav ---------- */
  const mobileNav = one('[data-mobile-nav]');
  function openMobileNav() {
    if (!mobileNav) return;
    mobileNav.setAttribute('data-open', 'true');
    overlay.show();
  }
  function closeMobileNav() {
    if (!mobileNav || mobileNav.getAttribute('data-open') !== 'true') return;
    mobileNav.setAttribute('data-open', 'false');
    if (!one('[data-drawer][data-open="true"]')) overlay.hide();
  }
  all('[data-mobile-nav-toggle]').forEach((btn) =>
    on(btn, 'click', () => (mobileNav && mobileNav.getAttribute('data-open') === 'true' ? closeMobileNav() : openMobileNav()))
  );
  all('[data-mobile-nav-close]').forEach((btn) => on(btn, 'click', closeMobileNav));

  /* ---------- Mega menu ---------- */
  function closeMegaMenus(except) {
    all('[data-mega-menu]').forEach((m) => {
      if (m === except) return;
      m.setAttribute('data-open', 'false');
    });
    all('[data-mega-trigger]').forEach((t) => {
      if (except && t.getAttribute('aria-controls') === except.id) return;
      t.setAttribute('aria-expanded', 'false');
    });
  }

  all('[data-mega-trigger]').forEach((trigger) => {
    const menu = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!menu) return;

    if (window.matchMedia('(hover: hover)').matches) {
      let timer;
      const show = () => {
        clearTimeout(timer);
        closeMegaMenus(menu);
        menu.setAttribute('data-open', 'true');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const hide = () => {
        timer = setTimeout(() => {
          menu.setAttribute('data-open', 'false');
          trigger.setAttribute('aria-expanded', 'false');
        }, 120);
      };
      on(trigger, 'mouseenter', show);
      on(trigger, 'mouseleave', hide);
      on(menu, 'mouseenter', () => clearTimeout(timer));
      on(menu, 'mouseleave', hide);
    }

    on(trigger, 'click', (e) => {
      e.preventDefault();
      const isOpen = menu.getAttribute('data-open') === 'true';
      closeMegaMenus();
      menu.setAttribute('data-open', isOpen ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });

  /* ---------- Scroll reveal ---------- */
  if ('IntersectionObserver' in window && document.body.classList.contains('animations')) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    all('.reveal').forEach((el) => io.observe(el));
  } else {
    all('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Quantity ---------- */
  function bindQuantity(scope) {
    all('[data-quantity]', scope).forEach((q) => {
      if (q.dataset.bound) return;
      q.dataset.bound = '1';
      const input = one('.quantity__input', q);
      on(q, 'click', (e) => {
        const btn = e.target.closest('.quantity__button');
        if (!btn || !input) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 0;
        const max = input.max === '' ? Infinity : Number(input.max);
        let val = Number(input.value) || 0;
        val += btn.hasAttribute('data-plus') ? step : -step;
        val = Math.min(Math.max(val, min), max);
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      on(input, 'change', () => {
        const min = Number(input.min) || 0;
        const max = input.max === '' ? Infinity : Number(input.max);
        let val = parseInt(input.value, 10);
        if (isNaN(val)) val = min;
        input.value = Math.min(Math.max(val, min), max);
      });
    });
  }
  bindQuantity();
  document.addEventListener('voltage:content:updated', (e) => bindQuantity(e.target));

  /* ---------- Facets: auto-submit ---------- */
  all('[data-facets-form]').forEach((form) => {
    on(form, 'change', (e) => {
      if (e.target.matches('input[type="checkbox"], input[type="radio"], select')) {
        form.querySelectorAll('input[type="hidden"]').forEach((h) => {
          if (h.name === 'page') h.remove();
        });
        form.submit();
      }
    });
  });

  /* ---------- Slideshow ---------- */
  all('[data-slideshow]').forEach((root) => {
    const slides = all('[data-slide]', root);
    if (slides.length < 1) return;
    const dots = all('[data-slide-dot]', root);
    const prev = one('[data-slide-prev]', root);
    const next = one('[data-slide-next]', root);
    const autoplayMs = Number(root.dataset.autoplay) || 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    let timer = null;

    function go(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        s.classList.toggle('is-active', n === index);
        s.setAttribute('aria-hidden', n === index ? 'false' : 'true');
      });
      dots.forEach((d, n) => d.setAttribute('aria-current', n === index ? 'true' : 'false'));
    }

    function start() {
      if (!autoplayMs || reduceMotion || slides.length < 2) return;
      stop();
      timer = setInterval(() => go(index + 1), autoplayMs);
    }
    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    on(prev, 'click', () => { go(index - 1); start(); });
    on(next, 'click', () => { go(index + 1); start(); });
    dots.forEach((d, n) => on(d, 'click', () => { go(n); start(); }));

    on(root, 'mouseenter', stop);
    on(root, 'mouseleave', start);
    on(root, 'focusin', stop);
    on(root, 'focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    go(0);
    start();
  });

  /* ---------- Gallery slider ---------- */
  all('[data-gallery]').forEach((gallery) => {
    const slides = all('[data-gallery-slide]', gallery);
    const thumbs = all('[data-gallery-thumb]', gallery);
    if (slides.length < 2) return;

    const prev = one('[data-gallery-prev]', gallery);
    const next = one('[data-gallery-next]', gallery);
    let index = 0;

    function go(n) {
      index = (n + slides.length) % slides.length;
      slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === index);
        s.setAttribute('aria-hidden', k === index ? 'false' : 'true');
      });
      thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === index);
        t.setAttribute('aria-selected', k === index ? 'true' : 'false');
      });
      if (thumbs[index]) {
        thumbs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    on(prev, 'click', () => go(index - 1));
    on(next, 'click', () => go(index + 1));
    thumbs.forEach((t) => on(t, 'click', () => go(Number(t.dataset.index))));

    on(gallery, 'keydown', (e) => {
      if (e.key === 'ArrowLeft') go(index - 1);
      if (e.key === 'ArrowRight') go(index + 1);
    });

    go(0);
  });

  /* ---------- Gallery lightbox ---------- */
  all('[data-gallery]').forEach((gallery) => {
    const items = all('[data-gallery-item]', gallery);
    if (!items.length) return;
    let mIndex = 0;

    const modal = document.createElement('div');
    modal.className = 'lightbox';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product image');
    modal.setAttribute('data-open', 'false');
    modal.innerHTML = `
      <button type="button" class="lightbox__close icon-button" aria-label="Close">${closeIcon()}</button>
      <button type="button" class="lightbox__nav lightbox__nav--prev icon-button" aria-label="Previous image">${chevronIcon('left')}</button>
      <img class="lightbox__img" alt="" loading="lazy">
      <button type="button" class="lightbox__nav lightbox__nav--next icon-button" aria-label="Next image">${chevronIcon('right')}</button>`;
    gallery.parentElement.appendChild(modal);

    const img = one('.lightbox__img', modal);

    function show(i) {
      mIndex = (i + items.length) % items.length;
      const src = items[mIndex].dataset.galleryItem;
      img.src = src;
      img.alt = one('img', items[mIndex])?.alt || '';
    }

    items.forEach((item, i) => {
      on(item, 'click', () => {
        show(i);
        modal.setAttribute('data-open', 'true');
        document.body.style.overflow = 'hidden';
      });
    });

    on(one('.lightbox__close', modal), 'click', () => {
      modal.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
    });
    on(one('.lightbox__nav--prev', modal), 'click', () => show(mIndex - 1));
    on(one('.lightbox__nav--next', modal), 'click', () => show(mIndex + 1));
    on(modal, 'click', (e) => {
      if (e.target === modal) {
        modal.setAttribute('data-open', 'false');
        document.body.style.overflow = '';
      }
    });
    on(document, 'keydown', (e) => {
      if (modal.getAttribute('data-open') !== 'true') return;
      if (e.key === 'ArrowLeft') show(mIndex - 1);
      if (e.key === 'ArrowRight') show(mIndex + 1);
    });
  });

  /* ---------- Share: copy link ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-link]');
    if (!btn) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(window.location.href);
      const original = btn.textContent;
      btn.textContent = btn.dataset.copiedLabel || 'Copied';
      btn.disabled = true;
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1800);
    } catch (_) {
      window.prompt('Copy link', window.location.href);
    }
  });

  /* ---------- Variant picker ---------- */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-variant-input]');
    if (!input) return;
    const form = input.closest('[data-product-form]');
    if (form) updateVariant(form);
  });

  function updateVariant(form) {
    const dataEl = one('[data-variant-data]', form);
    if (!dataEl) return;
    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (_) {
      return;
    }

    const selected = all('[data-variant-input]:checked', form).map((i) => i.value);
    const variant = variants.find(
      (v) => JSON.stringify(v.options) === JSON.stringify(selected)
    );

    // Price, stock and SKU usually sit outside the form, so search the whole product section
    const scope = form.closest('[data-section-id]') || form.parentElement || document;

    const idInput = one('[data-variant-id]', form);
    const addBtn = one('[data-add-button]', form);
    const priceEl = one('[data-variant-price]', scope);
    const skuEl = one('[data-variant-sku]', scope);
    const stockEl = one('[data-variant-stock]', scope);

    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = window.Voltage.strings.unavailable;
      }
      return;
    }

    if (idInput) idInput.value = variant.id;

    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available
        ? window.Voltage.strings.addToCart
        : window.Voltage.strings.soldOut;
    }

    if (priceEl && variant.price_html) priceEl.outerHTML = variant.price_html;
    if (skuEl) skuEl.textContent = variant.sku || '—';

    if (stockEl) {
      const low = variant.inventory_managed && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5;
      stockEl.className =
        'stock-status ' + (variant.available ? (low ? 'stock-status--low' : 'stock-status--in') : 'stock-status--out');
      stockEl.textContent = variant.available
        ? low
          ? `Only ${variant.inventory_quantity} left`
          : 'In stock, ships immediately'
        : 'Out of stock';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  // Initialise any product forms already on the page
  all('[data-product-form]').forEach(updateVariant);

  /* ---------- Sticky buy box ---------- */
  // A buy box taller than the viewport must not stick, or its lower half becomes unreachable
  (function stickyBuyBox() {
    const boxes = all('.product__info');
    if (!boxes.length) return;

    const OFFSET = 6 * 16; // Keeps in sync with top: 6rem in CSS
    const BUFFER = 32;

    const check = () => {
      const available = window.innerHeight - OFFSET - BUFFER;
      boxes.forEach((box) => box.classList.toggle('is-sticky', box.offsetHeight < available));
    };

    check();
    on(window, 'resize', check);
    on(window, 'load', check);
  })();

  /* ---------- Recently viewed ---------- */
  (function recentlyViewed() {
    const KEY = 'voltage:recently-viewed';
    const MAX = 12;

    const read = () => {
      try {
        const raw = localStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (_) {
        return [];
      }
    };

    const write = (list) => {
      try {
        localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      } catch (_) {
        /* localStorage is unwritable in private mode; nothing useful to do */
      }
    };

    const dataEl = one('[data-recent-product]');
    let currentItem = null;
    if (dataEl) {
      try {
        currentItem = JSON.parse(dataEl.textContent);
        const list = read().filter((p) => p.handle !== currentItem.handle);
        list.unshift(currentItem);
        write(list);
      } catch (_) {
        currentItem = null;
      }
    }

    const grid = one('[data-recently-viewed]');
    if (!grid) return;

    const limit = Number(grid.dataset.limit) || 4;
    const items = read()
      .filter((p) => !currentItem || p.handle !== currentItem.handle)
      .slice(0, limit);
    if (!items.length) return;

    grid.innerHTML = items
      .map(
        (p) => `
      <li class="product-card__item">
        <article class="product-card product-card--no-second">
          <a class="product-card__media product-card__media--square" href="${esc(p.url)}" aria-label="${esc(p.title)}">
            ${p.image ? `<img class="product-card__image product-card__image--primary" src="${esc(p.image)}" alt="${esc(p.imageAlt || p.title)}" loading="lazy" width="600" height="600">` : ''}
          </a>
          <div class="product-card__content">
            ${p.vendor ? `<p class="product-card__vendor">${esc(p.vendor)}</p>` : ''}
            <h3 class="product-card__title"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
            <div class="product-card__price">
              <div class="price"><span class="price__regular">${esc(p.price)}</span></div>
            </div>
          </div>
        </article>
      </li>`
      )
      .join('');

    const section = grid.closest('[data-recently-viewed-section]');
    if (section) section.removeAttribute('hidden');
  })();

  /* ---------- Icons ---------- */
  function chevronIcon(dir) {
    const d = dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 6l6 6-6 6';
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${d}"/></svg>`;
  }
  function closeIcon() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`;
  }

  window.Voltage = window.Voltage || {};
  window.Voltage.openDrawer = openDrawer;
  window.Voltage.closeDrawer = closeDrawer;
  window.Voltage.overlay = overlay;
})();
